import api_service, { buildQuery } from '../api/configurationAPIs';

const shouldFallback = (res) => {
  const status = Number(res?.status || 0);
  // Solo hacemos fallback cuando la ruta no existe.
  return status === 404;
};

const withFallback = async (primary, fallback) => {
  const res = await primary();
  if (shouldFallback(res)) {
    return fallback();
  }
  return res;
};

export const api = {

  // ─── Auth ───────────────────────────────────────────────
  auth: {
    login: (username, password) =>
      api_service.post('auth/login', { username, password }),

    session: () =>
      api_service.get('auth/session'),

    logout: () =>
      api_service.post('auth/logout', {}),
  },

  // ─── Dashboard ──────────────────────────────────────────
  dashboard: {
    /** GET /dashboard/completo?usuario=&fechaInicio=&fechaFin=&personaId= (sesión con credentials) */
    completo: (params = {}) =>
      api_service.get(`dashboard/completo${buildQuery(params)}`),

    metaVenta: (params = {}) =>
      api_service.get(`dashboard/graficas/meta-venta${buildQuery(params)}`),

    sucursalGraficas: (params = {}) =>
      api_service.get(`dashboard/graficas/sucursal${buildQuery(params)}`),
  },

  // ─── Cuentas ────────────────────────────────────────────
  cuentas: {
    metaMensual: (body = {}) =>
      withFallback(
        () => api_service.post('cuentas/meta-mensual', body),
        () => api_service.post('api/cuentas/meta-mensual', body),
      ),

    crearConfiguracionMeta: (body = {}) =>
      withFallback(
        () => api_service.post('cuentas/meta-punto-equilibrio', body),
        () => api_service.post('api/cuentas/meta-punto-equilibrio', body),
      ),

    actualizarMetaMensual: (id, body = {}) =>
      withFallback(
        () => api_service.patch(`cuentas/meta-mensual/${encodeURIComponent(id)}`, body),
        () => api_service.patch(`api/cuentas/meta-mensual/${encodeURIComponent(id)}`, body),
      ),

    actualizarConfiguracionMeta: (id, body = {}) =>
      withFallback(
        () => api_service.patch(`cuentas/meta-punto-equilibrio/${encodeURIComponent(id)}`, body),
        () => api_service.patch(`api/cuentas/meta-punto-equilibrio/${encodeURIComponent(id)}`, body),
      ),
  },

  // ─── Reportes ───────────────────────────────────────────
  reportes: {
    popularidad: (body = {}) =>
      api_service.post('reportes/popularidad', body),

    descuentosCortesias: (body = {}) =>
      api_service.post('reportes/descuentos-cortesias', body),

    cancelaciones: (params = {}) =>
      api_service.get(`reportes/cancelaciones${buildQuery(params)}`),

    gastos: (params = {}) =>
      api_service.get(`reportes/gastos${buildQuery(params)}`),

    gastosFiltrosAux: (params = {}) =>
      api_service.get(`reportes/gastos/filtros-aux${buildQuery(params)}`),

    tiposGastosCatalogo: (params = {}) =>
      api_service.get(`reportes/gastos/tipos-catalogo${buildQuery(params)}`),

    cajasCatalogo: (params = {}) =>
      api_service.get(`reportes/cajas/catalogo${buildQuery(params)}`),

    cancelacionesExport: (params = {}) =>
      api_service.get(`reportes/cancelaciones/export${buildQuery(params)}`, { responseType: 'blob' }),

    relacionVentas: (body = {}) =>
      api_service.post('reportes/relacion-ventas', body),

    ventasPorHora: (body = {}) =>
      api_service.post('reportes/ventas-por-hora', body),

    productosVendidosPorMesero: (body = {}) =>
      api_service.post('reportes/report-products-sold-by-waiter', body),

    ingresoPorDia: (body = {}) =>
      api_service.post('reportes/ingresos', body),

    exportar: (endpoint, formato = 'excel', body = {}) =>
      api_service.post(`reportes/${endpoint}/export`, { ...body, formato }, { responseType: 'blob' }),
  },

  Users: {
    /** POST /users/alta-admin */
    crear: (body = {}) =>
      withFallback(
        () => api_service.post('users/alta-admin', body),
        () => api_service.post('api/users/alta-admin', body),
      ),

    /** PUT /users/update/:userId */
    actualizar: (userId, body = {}) =>
      withFallback(
        () => api_service.put(`users/update/${encodeURIComponent(userId)}`, body),
        () => api_service.put(`api/users/update/${encodeURIComponent(userId)}`, body),
      ),

    /** DELETE /users/alta-admin */
    eliminar: (userId) =>
      withFallback(
        () => api_service.delete(`users/delete/${encodeURIComponent(userId)}`),
        () => api_service.delete(`api/users/delete/${encodeURIComponent(userId)}`),
      ),

    getById: (userId) =>
      withFallback(
        () => api_service.get(`users/get-by-id/${encodeURIComponent(userId)}`),
        () => api_service.get(`api/users/get/${encodeURIComponent(userId)}`),
      ),

    /** GET /users/empresa/cuenta — empresa de la sesión (p. ej. EmpresaID para roles) */
    empresaCuenta: () =>
      withFallback(
        () => api_service.get('users/empresa/cuenta'),
        () => api_service.get('api/users/empresa/cuenta'),
      ),

    /**
     * GET /users/listado/empresa/:empresaId — admins web de la empresa;
     * `empresaId` debe coincidir con la sesión (validación en backend).
     */
    getByEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`users/listado/empresa/${encodeURIComponent(empresaId)}`),
        () => api_service.get(`api/users/listado/empresa/${encodeURIComponent(empresaId)}`),
      ),

    /**
     * GET /users/users/search/empresa/:empresaId?page=&query=
     * Respuesta paginada: { data, page, pageSize, total, totalPages }.
     * @param {string} empresaId
     * @param {string|{ query?: string, page?: number }} queryOrOpts — string legacy = solo query; objeto = query + page (≥1).
     */
    searchByEmpresa: (empresaId, queryOrOpts = '') => {
      const opts =
        typeof queryOrOpts === 'object' && queryOrOpts !== null && !Array.isArray(queryOrOpts)
          ? queryOrOpts
          : { query: queryOrOpts };
      const q = String(opts.query ?? '').trim();
      const pageNum = Number(opts.page);
      const page = Number.isFinite(pageNum) && pageNum >= 1 ? Math.floor(pageNum) : 1;
      const params = { page };
      if (q) params.query = q;
      const config = { params };
      return withFallback(
        () => api_service.get(`users/users/search/empresa/${encodeURIComponent(empresaId)}`, config),
        () => api_service.get(`api/users/users/search/empresa/${encodeURIComponent(empresaId)}`, config),
      );
    },
  },

  tiposCuentas: {
    /**
     * GET /users/tipo-cuenta/listado — [{ tipoCuenta, clave, nombre }, …]
     * Evita 304 sin cuerpo: el navegador/axios puede dejar `data` vacío y el select queda sin opciones.
     */
    listar: () => {
      const bust = { params: { _nocache: Date.now() }, headers: { 'Cache-Control': 'no-cache' } };
      return withFallback(
        () => api_service.get('users/tipo-cuenta/listado', bust),
        () => api_service.get('api/users/tipo-cuenta/listado', bust),
      );
    },
  },

  roles: {
    /** GET /users/roles/listado-options/:empresaId — opciones simples para selects */
    getListadoOptionsByEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`users/roles/listado-options/${encodeURIComponent(empresaId)}`),
        () => api_service.get(`api/users/roles/listado-options/${encodeURIComponent(empresaId)}`),
      ),

    /** GET roles filtrados por empresa */
    getByEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`users/roles/empresa/${encodeURIComponent(empresaId)}`),
        () => api_service.get(`api/users/roles/empresa/${encodeURIComponent(empresaId)}`),
      ),

    /**
     * GET /users/roles/search/empresa/:empresaId?page=&query=
     * Respuesta paginada: { data, page, pageSize, total, totalPages }.
     */
    searchByEmpresa: (empresaId, queryOrOpts = '') => {
      const opts =
        typeof queryOrOpts === 'object' && queryOrOpts !== null && !Array.isArray(queryOrOpts)
          ? queryOrOpts
          : { query: queryOrOpts };
      const q = String(opts.query ?? '').trim();
      const pageNum = Number(opts.page);
      const page = Number.isFinite(pageNum) && pageNum >= 1 ? Math.floor(pageNum) : 1;
      const params = { page };
      if (q) params.query = q;
      const config = { params };
      return withFallback(
        () => api_service.get(`users/roles/search/empresa/${encodeURIComponent(empresaId)}`, config),
        () => api_service.get(`api/users/roles/search/empresa/${encodeURIComponent(empresaId)}`, config),
      );
    },



    /** POST { nombre, tipoCuenta } */
    crear: (body = {}) =>
      withFallback(
        () => api_service.post('users/roles/alta', body),
        () => api_service.post('api/users/roles/alta', body),
      ),

    /** DELETE rol */
    eliminar: (rolId) =>
      withFallback(
        () => api_service.delete(`users/roles/${encodeURIComponent(rolId)}`),
        () => api_service.delete(`api/users/roles/${encodeURIComponent(rolId)}`),
      ),

    /** GET rol */
    obtener: (rolId) =>
      withFallback(
        () => api_service.get(`users/roles/${encodeURIComponent(rolId)}`),
        () => api_service.get(`api/users/roles/${encodeURIComponent(rolId)}`),
      ),

    /** PATCH rol existente */
    actualizar: (rolId, body = {}) =>
      withFallback(
        () => api_service.patch(`users/roles/${encodeURIComponent(rolId)}`, body),
        () => api_service.patch(`api/users/roles/${encodeURIComponent(rolId)}`, body),
      ),
  },

  permisos: {
    /**
     * GET catálogo global de permisos activos (users/permisos/listado | users/permisos).
     * Misma lógica que el backend @Get(['permisos', 'permisos/listado']).
     */
    listado: () => {
      const bust = { params: { _nocache: Date.now() }, headers: { 'Cache-Control': 'no-cache' } };
      return withFallback(
        () => api_service.get('users/permisos/listado', bust),
        () =>
          withFallback(
            () => api_service.get('users/permisos', bust),
            () => api_service.get('api/users/permisos/listado', bust),
          ),
      );
    },

    /** GET permisos filtrados por empresa */
    getByEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`users/permisos/empresa/${encodeURIComponent(empresaId)}`),
        () => api_service.get(`api/users/permisos/empresa/${encodeURIComponent(empresaId)}`),
      ),
  },

  getSucursales: {
    /**
     * GET /sucursales/empresa/:empresaId — sucursales del alcance de la cuenta en sesión
     * (subusuario: solo las asignadas en CuentaSucursales; cuenta empresa: todas las de la empresa).
     */
    byEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`sucursales/empresa/${encodeURIComponent(empresaId)}`),
        () => api_service.get(`api/sucursales/empresa/${encodeURIComponent(empresaId)}`),
      ),

    updateSucursal: (sucursalId, body = {}) =>
      withFallback(
        () => api_service.patch(`sucursales/update/${encodeURIComponent(sucursalId)}`, body),
        () => api_service.patch(`api/sucursales/update/${encodeURIComponent(sucursalId)}`, body),
      ),
  },

  empresas: {
    /** GET /empresa/usuario/:personaId — empresa asociada al usuario */
    findEmpresaByUsuario: (personaId) =>
      withFallback(
        () => api_service.get(`empresas/usuario/${encodeURIComponent(personaId)}`),
        () => api_service.get(`api/empresas/usuario/${encodeURIComponent(personaId)}`),
      ),

    updateEmpresa: (empresaId, body = {}) =>
      withFallback(
        () => api_service.patch(`empresas/update/${encodeURIComponent(empresaId)}`, body),
        () => api_service.patch(`api/empresas/update/${encodeURIComponent(empresaId)}`, body),
      ),
  },

  elementosNavegacion: {
    /**
     * GET users/elementos-navegacion/empresa/:empresaId — listado de elementos de navegación de la empresa;
     * `empresaId` (UUID) debe coincidir con el scope de la sesión.
     */
    byEmpresa: (empresaId) =>
      withFallback(
        () => api_service.get(`elementos-navegacion/get-permisos`),
        () => api_service.get(`api/elementos-navegacion/get-permisos`),
      ),

  },

};

export default api;
